import React from 'react';
import PropTypes from "prop-types";
import { GoogleMapContext } from "../GoogleMap";
import {createPolylineFromLatLngList, decodePolyline, createLatLng, distanceBetweenPoints} from "../../google";

const fillColor = '#5bce61';
const borderColor = '#4aa84f';
const middleColor = '#4eb153';

const routePropType = PropTypes.shape({
  dynamic: PropTypes.shape({
    visibility: PropTypes.bool,
    polylines: PropTypes.arrayOf(PropTypes.string),
  }),
  static: PropTypes.shape({
    visibility: PropTypes.bool,
    polylines: PropTypes.arrayOf(PropTypes.string),
  }),
});

export default class Polyline extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      polylines: [],
    };
  }

  static propTypes = {
    route: routePropType,
    driverLocation: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    snappedSegment: PropTypes.arrayOf(PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    })),
  };

  static contextType = GoogleMapContext;

  clearExistingPolyline() {
    this.state.polylines.forEach(polyline => {
      polyline.setMap(null);
    });
    this.setState(prevState => ({
      polylines: []
    }));
  }

  getDriverLocToDestCoordinates(encodedPath, driverLocation, snappedSegment) {
    const polylineCoordinates = decodePolyline(encodedPath);
    if (snappedSegment == null) {
      return polylineCoordinates
    }
    const snappedSegmentLatLngFirst = createLatLng(snappedSegment[0].latitude, snappedSegment[0].longitude);
    const snappedSegmentLatLngSecond = createLatLng(snappedSegment[1].latitude, snappedSegment[1].longitude);
    const currentSegmentIndex = polylineCoordinates.findIndex((segment, index) =>
      (distanceBetweenPoints(segment, snappedSegmentLatLngFirst) <= 1) &&
      (index+1 < polylineCoordinates.length ? (distanceBetweenPoints(polylineCoordinates[index+1], snappedSegmentLatLngSecond) <= 1) : true));
    const driverLocationLatLng = createLatLng(driverLocation.latitude, driverLocation.longitude);
    const driverLocToDestCoordinates = [driverLocationLatLng].concat(polylineCoordinates.slice(currentSegmentIndex+1));
    return driverLocToDestCoordinates;
  }

  addPolylineToMap(coordinates, color, weight, context=this.context) {
    var polyline = createPolylineFromLatLngList(coordinates);
    polyline.setOptions({
      strokeColor: color,
      strokeWeight: weight,
      geodesic: true,
    });
    polyline.setMap(context);
    return polyline;
  }

  getDynamicPolyline() {
    if (this.props.route.dynamic && this.props.route.dynamic.visibility) {
      const encodedPath = this.props.route.dynamic.polylines[0];
      return this.getDriverLocToDestCoordinates(encodedPath, this.props.driverLocation, this.props.snappedSegment);
    }
    return null;
  }

  getStaticPolylines() {
    if(this.props.route.static && this.props.route.static.visibility) {
      return this.props.route.static.polylines;
    }
    return [];
  }

  createAndSetPolylineToMap() {
    this.clearExistingPolyline();

    const dynamicPolyline = this.getDynamicPolyline();
    if (dynamicPolyline) {
      const borderPolyline = this.addPolylineToMap(dynamicPolyline, borderColor, 4);
      const middlePolyline = this.addPolylineToMap(dynamicPolyline, middleColor, 3);
      const fillPolyline = this.addPolylineToMap(dynamicPolyline, fillColor, 2);
      this.setState(prevState => ({
        polylines: [...prevState.polylines, borderPolyline, middlePolyline, fillPolyline]
      }));
    }

    const staticPolylines = this.getStaticPolylines();
    staticPolylines.forEach(polyline => {
      const polylineCoordinates = decodePolyline(polyline);
      const borderPolyline = this.addPolylineToMap(polylineCoordinates, borderColor, 4);
      const middlePolyline = this.addPolylineToMap(polylineCoordinates, middleColor, 3);
      const fillPolyline = this.addPolylineToMap(polylineCoordinates, fillColor, 2);
      this.setState(prevState => ({
        polylines: [...prevState.polylines, borderPolyline, middlePolyline, fillPolyline]
      }));
    })

  }

  componentDidMount() {
    this.createAndSetPolylineToMap();
  }

  checkIfRouteChanged = (oldRoute, newRoute) => {
    if((oldRoute.static ^ newRoute.static) || (oldRoute.dynamic ^ newRoute.dynamic)){
      return true;
    }
    if((oldRoute.static && newRoute.static) && (oldRoute.static.visibility !== newRoute.static.visibility && oldRoute.static.polylines.length !== newRoute.static.polylines.length)){
      return true;
    }
    if((oldRoute.dynamic && newRoute.dynamic) && (oldRoute.dynamic.visibility !== newRoute.dynamic.visibility || oldRoute.dynamic.polylines[0] !== newRoute.dynamic.polylines[0])){
      return true;
    }
    return false;
  }

  checkIfVehicleLocationChanged = (oldLocation, newLocation) => {
    return (oldLocation.latitude !== newLocation.latitude || oldLocation.longitude !== newLocation.longitude);
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    return this.checkIfRouteChanged(this.props.route, nextProps.route) || this.checkIfVehicleLocationChanged(this.props.driverLocation, nextProps.driverLocation);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.createAndSetPolylineToMap();
  }

  componentWillUnmount() {
    this.clearExistingPolyline();
  }

  render() {
    return null;
  }
}
